import React, { useState, useEffect, useCallback } from 'react';
import { Folder, FolderOpen, File, ChevronRight, ChevronDown, Plus, RefreshCw, Download, Search, FolderPlus, X, Check, MoreVertical, Edit2, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const getFileIcon = (ext) => {
  const colors = { js: 'text-yellow-400', jsx: 'text-cyan-400', ts: 'text-blue-400', tsx: 'text-blue-400', py: 'text-green-400', html: 'text-orange-400', css: 'text-pink-400', json: 'text-yellow-300' };
  return colors[ext?.toLowerCase()] || 'text-gray-400';
};

const FileExplorer = ({ projectId, projectName, onFileSelect, selectedFile }) => {
  const [tree, setTree] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set(['']));
  const [newItem, setNewItem] = useState({ show: false, isDir: false, name: '' });
  const [search, setSearch] = useState('');

  const fetchTree = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('cloudtexa_token');
      const res = await axios.get(`${API}/api/files/tree/${projectId}`, { headers: { Authorization: `Bearer ${token}` } });
      setTree(res.data.tree);
      setStats(res.data.stats);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetchTree(); }, [fetchTree]);

  const toggleExpand = (path) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(path) ? next.delete(path) : next.add(path);
      return next;
    });
  };

  const handleSelect = async (node) => {
    if (node.is_directory) { toggleExpand(node.path); return; }
    try {
      const token = localStorage.getItem('cloudtexa_token');
      const res = await axios.get(`${API}/api/files/${projectId}/${node.path}`, { headers: { Authorization: `Bearer ${token}` } });
      onFileSelect(res.data);
    } catch (e) { toast.error('Error al cargar'); }
  };

  const createItem = async () => {
    if (!newItem.name.trim()) return;
    try {
      const token = localStorage.getItem('cloudtexa_token');
      const parent = selectedFile?.is_directory ? selectedFile.path : (selectedFile?.parent_path || '');
      const path = parent ? `${parent}/${newItem.name}` : newItem.name;
      await axios.post(`${API}/api/files`, { project_id: projectId, path, content: newItem.isDir ? undefined : '', is_directory: newItem.isDir }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Creado');
      setNewItem({ show: false, isDir: false, name: '' });
      fetchTree();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('¿Eliminar?')) return;
    try {
      const token = localStorage.getItem('cloudtexa_token');
      await axios.delete(`${API}/api/files/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Eliminado');
      fetchTree();
    } catch (e) { toast.error('Error'); }
  };

  const extractFromChat = async () => {
    try {
      const token = localStorage.getItem('cloudtexa_token');
      const res = await axios.post(`${API}/api/files/from-chat/${projectId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      fetchTree();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const renderNode = (node, level = 0) => {
    const isExp = expanded.has(node.path);
    const isSel = selectedFile?.path === node.path;
    return (
      <div key={node.id || node.path}>
        <div className={`group flex items-center gap-1 py-1 px-2 cursor-pointer ${isSel ? 'bg-cyan-500/20 text-cyan-400' : 'hover:bg-white/5 text-gray-300'}`} style={{ paddingLeft: `${level * 12 + 8}px` }} onClick={() => handleSelect(node)}>
          {node.is_directory && <span className="w-4">{isExp ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}</span>}
          {!node.is_directory && <span className="w-4" />}
          {node.is_directory ? (isExp ? <FolderOpen className="w-4 h-4 text-amber-400" /> : <Folder className="w-4 h-4 text-amber-400" />) : <File className={`w-4 h-4 ${getFileIcon(node.extension)}`} />}
          <span className="flex-1 text-sm truncate">{node.name}</span>
          <button onClick={(e) => { e.stopPropagation(); deleteItem(node.id); }} className="p-1 opacity-0 group-hover:opacity-100 hover:bg-red-500/20 rounded text-red-400"><Trash2 className="w-3 h-3" /></button>
        </div>
        {node.is_directory && isExp && node.children?.map(c => renderNode(c, level + 1))}
      </div>
    );
  };

  if (loading) return <div className="h-full flex items-center justify-center"><RefreshCw className="w-5 h-5 animate-spin text-gray-400" /></div>;

  return (
    <div className="h-full flex flex-col bg-slate-900/50 border-r border-slate-700/50" data-testid="file-explorer">
      <div className="p-3 border-b border-slate-700/50">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-medium text-white text-sm truncate">{projectName || 'Archivos'}</h3>
          <div className="flex gap-1">
            <button onClick={() => setNewItem({ show: true, isDir: false, name: '' })} className="p-1.5 rounded hover:bg-white/10 text-gray-400" title="Nuevo archivo"><Plus className="w-4 h-4" /></button>
            <button onClick={() => setNewItem({ show: true, isDir: true, name: '' })} className="p-1.5 rounded hover:bg-white/10 text-gray-400" title="Nueva carpeta"><FolderPlus className="w-4 h-4" /></button>
            <button onClick={extractFromChat} className="p-1.5 rounded hover:bg-white/10 text-gray-400" title="Extraer del chat"><Download className="w-4 h-4" /></button>
            <button onClick={fetchTree} className="p-1.5 rounded hover:bg-white/10 text-gray-400" title="Refrescar"><RefreshCw className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar..." className="w-full pl-8 pr-3 py-1.5 bg-slate-800 border border-slate-700 rounded text-sm text-white placeholder-gray-500 outline-none" />
        </div>
      </div>
      {newItem.show && (
        <div className="p-2 flex items-center gap-2 border-b border-slate-700/50">
          {newItem.isDir ? <Folder className="w-4 h-4 text-amber-400" /> : <File className="w-4 h-4 text-gray-400" />}
          <input type="text" value={newItem.name} onChange={e => setNewItem({ ...newItem, name: e.target.value })} placeholder={newItem.isDir ? "carpeta" : "archivo.js"} className="flex-1 bg-slate-800 px-2 py-1 rounded text-sm text-white outline-none" autoFocus onKeyDown={e => { if (e.key === 'Enter') createItem(); if (e.key === 'Escape') setNewItem({ show: false, isDir: false, name: '' }); }} />
          <button onClick={createItem} className="p-1 text-green-400"><Check className="w-4 h-4" /></button>
          <button onClick={() => setNewItem({ show: false, isDir: false, name: '' })} className="p-1 text-gray-400"><X className="w-4 h-4" /></button>
        </div>
      )}
      <div className="flex-1 overflow-y-auto py-1">
        {tree?.children?.length > 0 ? tree.children.map(n => renderNode(n)) : (
          <div className="text-center py-8 px-4">
            <Folder className="w-12 h-12 mx-auto mb-3 text-gray-600" />
            <p className="text-gray-500 text-sm mb-2">Proyecto vacío</p>
            <button onClick={extractFromChat} className="text-cyan-400 text-sm hover:underline">Extraer código del chat</button>
          </div>
        )}
      </div>
      {stats && <div className="px-3 py-2 border-t border-slate-700/50 text-xs text-gray-500 flex justify-between"><span>{stats.total_files} archivos</span><span>{stats.total_size_formatted}</span></div>}
    </div>
  );
};

export default FileExplorer;
